import { motion } from "framer-motion";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useEffect, useRef, useState } from "react";
import {
    FaBalanceScale,
    FaChartLine,
    FaCrown,
    FaDatabase,
    FaEye,
    FaLeaf,
    FaLock,
    FaRobot,
    FaShieldAlt,
    FaUsers
} from "react-icons/fa";
import {
    TiArrowLeft,
    TiArrowRight,
    TiArrowUp,
    TiChartLineOutline,
    TiChartPie,
    TiChevronRight,
    TiFlash,
    TiLightbulb,
    TiRadarOutline,
    TiTick
} from "react-icons/ti";
import { useNavigate } from "react-router-dom";
import AnimatedTitle from "./AnimatedTitle";
import Footer from "./Footer";
import ScrollDownButton from './ScrollDownButton';
import GlassButton, { GlassIconButton } from './ui/GlassButton';
import { SparklesCore } from './ui/SparklesCore';

gsap.registerPlugin(ScrollTrigger);

const Portfolio = () => {
  const [scrollDownClicked, setScrollDownClicked] = useState(false);
  const [isBottom, setIsBottom] = useState(false);
  const navigate = useNavigate();

  const sectionRefs = useRef([]);
  const scrollDownRef = useRef(null);
  const scrollTopRef = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    sectionRefs.current.forEach((section, index) => {
      if (section) {
        gsap.fromTo(
          section,
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 1,
            delay: index * 0.1,
            scrollTrigger: {
              trigger: section,
              start: "top 85%",
            },
          }
        );
      }
    });

    if (scrollDownRef.current) {
      gsap.fromTo(
        scrollDownRef.current,
        { opacity: 0, y: -20 },
        { opacity: 1, y: 0, duration: 1 }
      );
    }
  }, []);

  const checkIfBottom = () => {
    if (window.innerHeight + window.scrollY >= document.body.offsetHeight - 200) {
      setIsBottom(true);
    } else {
      setIsBottom(false);
    }
  };

  const scrollToContent = () => {
    const content = document.getElementById("content");
    if (content) {
      const contentOffsetTop = content.offsetTop;
      window.scrollTo({
        top: contentOffsetTop - 100,
        behavior: "smooth",
      });
    }
    setScrollDownClicked(true);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setScrollDownClicked(false);
  };

  useEffect(() => {
    window.addEventListener("scroll", checkIfBottom);
    return () => {
      window.removeEventListener("scroll", checkIfBottom);
    };
  }, []);

  const performanceStats = [
    { value: "+18.4%", label: "Annualised ROI", icon: <FaChartLine className="text-cyan-400" />, sub: "Outperforming Market" },
    { value: "9.4/10", label: "Risk Mitigation", icon: <FaShieldAlt className="text-purple-400" />, sub: "Secure & Diversified" },
    { value: "Platinum", label: "ESG Global Impact", icon: <FaLeaf className="text-emerald-400" />, sub: "Top 5% Ethical Finance" },
    { value: "Active", label: "Auto-Rebalancing", icon: <FaRobot className="text-orange-400" />, sub: "Optimising 24/7" },
  ];

  const intelligencePillars = [
    {
      title: "Hybrid Advisory",
      desc: "Merge machine precision with human financial expertise for a balanced investment strategy.",
      icon: <FaUsers />,
      color: "cyan",
      tag: "Synergistic"
    },
    {
      title: "Goal-Centric Design",
      desc: "Custom portfolios tailored for retirement, education, or long-term ecological impact goals.",
      icon: <FaCrown />,
      color: "purple",
      tag: "Personalised"
    },
    {
      title: "Predictive Analytics",
      desc: "AI-driven forecasting models that identify market trends before they manifest.",
      icon: <TiLightbulb />,
      color: "orange",
      tag: "Forward-Looking"
    },
    {
      title: "Institutional Security",
      desc: "AES-256 encryption and multi-sig custodial solutions protect your digital wealth legacy.",
      icon: <FaLock />,
      color: "emerald",
      tag: "Immutable"
    }
  ];

  const analyticsFeatures = [
    {
      title: "Real-Time Risk Analysis",
      description: "Continuous monitoring of market volatility and portfolio exposure with automated alerts.",
      icon: <TiRadarOutline />,
      features: ["Monte Carlo Simulations", "Stress Testing", "Correlation Analysis"]
    },
    {
      title: "Yield Generation Hub",
      description: "Optimized staking and liquidity provision across multi-chain ecosystems for maximum APY.",
      icon: <TiChartPie />,
      features: ["Auto-Compounder", "Multi-Chain Yield Aggregator", "Gas Optimization"]
    }
  ];

  return (
    <div className="w-screen min-h-screen bg-black text-blue-50 font-sans">
      {/* Hero Section */}
      <div className="relative w-full h-screen flex flex-col items-center justify-center p-5">
        <div className="relative w-full max-w-6xl h-64 md:h-[500px] overflow-hidden rounded-[4rem] border border-white/10 z-10 shadow-[0_0_80px_rgba(34,211,238,0.15)] bg-black">
          <video
            src="/videos/portfolio-hero.mp4"
            autoPlay
            loop
            muted
            playsInline
            className="absolute left-0 top-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black" />
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <SparklesCore
              id="portfolioHeroSparkles"
              background="transparent"
              minSize={0.6}
              maxSize={1.4}
              particleDensity={40}
              particleColor="#22d3ee"
              className="absolute inset-0"
            />
            <AnimatedTitle
              title="Mastering Financial Destiny"
              containerClass="text-center text-blue-50 font-zentry relative z-10"
            />
            <motion.p 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="text-cyan-400/80 font-bold tracking-[0.4em] uppercase text-[10px] mt-6 z-10"
            >
              AI-Driven Portfolio Optimisation & Hybrid Advisory
            </motion.p>
          </div>
        </div>
      </div>

      {/* Scroll Down Button */}
      {!scrollDownClicked && (
        <ScrollDownButton
          ref={scrollDownRef}
          onClick={scrollToContent}
        />
      )}

      {/* Content Section */}
      <div id="content" className="px-6 md:px-10 py-32 mx-auto max-w-7xl">
        
        {/* Wealth Performance Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-48" ref={(el) => (sectionRefs.current[0] = el)}>
           {performanceStats.map((stat, index) => (
             <motion.div
               key={index}
               initial={{ opacity: 0, y: 20 }}
               whileInView={{ opacity: 1, y: 0 }}
               viewport={{ once: true }}
               transition={{ delay: index * 0.1 }}
               className="p-8 bg-white/5 border border-white/10 rounded-[2.5rem] hover:border-cyan-500/40 transition-all group relative overflow-hidden"
             >
                <div className="absolute -top-10 -right-10 w-32 h-32 bg-white/5 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="text-4xl mb-6 group-hover:scale-110 transition-transform origin-left">{stat.icon}</div>
                <div className="text-xs text-gray-500 uppercase tracking-widest font-bold mb-2">{stat.label}</div>
                <div className="text-3xl font-bold text-white mb-2">{stat.value}</div>
                <div className="text-[10px] text-emerald-400/80 font-medium flex items-center gap-2 italic">
                   <TiFlash className="text-sm" /> {stat.sub}
                </div>
             </motion.div>
           ))}
        </div>

        {/* Intelligence Pillars Section */}
        <div className="grid lg:grid-cols-2 gap-20 mb-48 items-center" ref={(el) => (sectionRefs.current[1] = el)}>
          <div className="relative">
            <div className="absolute -top-20 -left-20 w-64 h-64 bg-purple-500/10 rounded-full blur-[100px]" />
            <div className="flex items-center gap-3 text-purple-400 font-bold uppercase tracking-[0.3em] text-[10px] mb-8">
              <span className="w-12 h-px bg-purple-400"></span>
              Wealth Intelligence
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-10 leading-[1.15]">
              Institutional <span className="text-purple-400 italic font-zentry uppercase tracking-tight">Precision</span> for Every <span className="text-purple-400 italic font-zentry uppercase tracking-tight">Investor</span>
            </h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-10">
              The Zero-Portfolio suite democratizes access to elite wealth management tools. 
              Our AI engines process global market sentiment in real-time, delivering insights 
              that were previously reserved for the world's largest hedge funds.
            </p>
            <div className="flex flex-wrap gap-4">
               <span className="px-4 py-2 bg-white/5 border border-white/10 rounded-full text-[10px] font-bold tracking-widest uppercase text-gray-300">ESG Compliant</span>
               <span className="px-4 py-2 bg-white/5 border border-white/10 rounded-full text-[10px] font-bold tracking-widest uppercase text-gray-300">Multi-Chain Tracking</span>
               <span className="px-4 py-2 bg-white/5 border border-white/10 rounded-full text-[10px] font-bold tracking-widest uppercase text-gray-300">AES-256 Secure</span>
            </div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
             {intelligencePillars.map((pillar, index) => (
                <motion.div
                  key={index}
                  whileHover={{ y: -5, backgroundColor: "rgba(255, 255, 255, 0.08)" }}
                  className="p-10 bg-white/5 border border-white/10 rounded-[3rem] transition-all cursor-default group"
                >
                  <div className={`w-14 h-14 rounded-2xl bg-${pillar.color}-500/10 flex items-center justify-center text-3xl text-${pillar.color}-400 mb-8 border border-white/5 group-hover:scale-110 transition-transform`}>
                    {pillar.icon}
                  </div>
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-bold text-white text-lg tracking-tight">{pillar.title}</h4>
                    <span className="text-[8px] uppercase tracking-tighter px-2 py-0.5 rounded-full bg-white/10 text-gray-500 border border-white/5">{pillar.tag}</span>
                  </div>
                  <p className="text-gray-500 text-sm leading-relaxed">{pillar.desc}</p>
                </motion.div>
             ))}
          </div>
        </div>

        {/* Predictive Analytics Visual */}
        <div className="mb-48" ref={(el) => (sectionRefs.current[2] = el)}>
          <div className="text-center mb-24">
            <h3 className="text-3xl md:text-4xl font-bold text-white mb-4 uppercase tracking-wider">Predictive Market Intelligence</h3>
            <p className="text-gray-300 max-w-2xl mx-auto">Harness AI-driven forecasts to identify growth opportunities and mitigate risks before the market shifts.</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
             {analyticsFeatures.map((feature, index) => (
                <div key={index} className="p-12 bg-white/5 border border-white/10 rounded-[4rem] group hover:border-cyan-500/30 transition-all relative overflow-hidden h-full">
                   <div className="absolute top-0 right-0 p-10 opacity-5 text-[10rem] pointer-events-none group-hover:scale-110 transition-transform">
                      {feature.icon}
                   </div>
                   <div className="relative z-10">
                      <div className="w-16 h-16 rounded-3xl bg-white/5 flex items-center justify-center text-4xl text-cyan-400 mb-10 border border-white/5 group-hover:bg-cyan-500/10 transition-all">
                        {feature.icon}
                      </div>
                      <h4 className="text-2xl font-bold text-white mb-6 uppercase tracking-widest">{feature.title}</h4>
                      <p className="text-gray-300 text-lg leading-relaxed mb-10">{feature.description}</p>
                      <ul className="space-y-4">
                         {feature.features.map((f, i) => (
                            <li key={i} className="flex items-center gap-3 text-gray-500 text-sm font-medium">
                               <TiTick className="text-cyan-400 text-lg" /> {f}
                            </li>
                         ))}
                      </ul>
                   </div>
                </div>
             ))}
          </div>
        </div>

        {/* Portfolio Lifecycle Flow */}
        <div className="mb-48 p-12 md:p-24 bg-white/5 border border-white/10 rounded-[4rem] relative overflow-hidden" ref={(el) => (sectionRefs.current[3] = el)}>
           <div className="absolute -bottom-20 -right-20 w-96 h-96 bg-cyan-500/5 rounded-full blur-[150px]" />
           
           <div className="relative z-10">
              <div className="flex flex-col md:flex-row items-center justify-between mb-24 gap-12">
                 <div className="max-w-xl">
                    <h3 className="text-3xl md:text-5xl font-bold text-white mb-8 leading-tight">
                       The Path to <span className="text-cyan-400 font-zentry italic uppercase tracking-widest">Optimized Wealth</span>
                    </h3>
                    <p className="text-gray-300 text-lg">A seamless, automated lifecycle designed to maximize yield and minimize volatility.</p>
                 </div>
                 <div className="flex gap-4">
                    <GlassIconButton onClick={() => {}} variant="gray">
                       <TiArrowLeft size={24} />
                    </GlassIconButton>
                    <GlassIconButton onClick={() => {}} variant="cyan">
                       <TiArrowRight size={24} />
                    </GlassIconButton>
                 </div>
              </div>

              <div className="grid md:grid-cols-4 gap-8">
                {[
                  { step: "Risk Analysis", icon: <TiRadarOutline />, color: "purple" },
                  { step: "Asset Selection", icon: <FaDatabase />, color: "cyan" },
                  { step: "AI Optimization", icon: <FaRobot />, color: "orange" },
                  { step: "Yield Generation", icon: <TiChartLineOutline />, color: "emerald" }
                ].map((item, idx) => (
                   <div key={idx} className="relative p-10 bg-black/40 border border-white/10 rounded-[3rem] text-center group hover:bg-white/5 transition-all">
                      <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-white/10 border border-white/10 text-[8px] font-bold text-gray-500 uppercase tracking-widest">Stage 0{idx+1}</div>
                      <div className={`w-16 h-16 rounded-full bg-${item.color}-500/10 flex items-center justify-center text-3xl text-${item.color}-400 mx-auto mb-8 border border-white/5 group-hover:scale-110 transition-transform`}>
                         {item.icon}
                      </div>
                      <h4 className="text-white font-bold tracking-wide">{item.step}</h4>
                      {idx < 3 && <TiChevronRight className="absolute -right-4 top-1/2 -translate-y-1/2 text-2xl text-gray-700 hidden md:block" />}
                   </div>
                ))}
              </div>
           </div>
        </div>

        {/* Final CTA */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="relative rounded-[4rem] overflow-hidden border border-white/10 p-1"
          ref={(el) => (sectionRefs.current[4] = el)}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-purple-500/20 via-blue-900/40 to-cyan-500/20" />
          <div className="absolute inset-0 bg-black/80 backdrop-blur-3xl" />
          
          <div className="relative z-10 py-32 px-8 text-center max-w-4xl mx-auto">
            <div className="w-24 h-24 rounded-[2.5rem] bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-5xl text-purple-400 mx-auto mb-12 shadow-[0_0_50px_rgba(168,85,247,0.2)]">
              <FaBalanceScale />
            </div>
            <h3 className="text-4xl md:text-5xl font-bold text-white mb-10 leading-tight">
              Expand Your <span className="font-zentry italic text-purple-400 text-6xl uppercase tracking-wider">Wealth Frontier</span>
            </h3>
            <p className="text-gray-300 mb-16 text-lg leading-relaxed max-w-2xl mx-auto">
              Join the institutional-grade movement. Build your custom ESG portfolio, 
              leverage AI market intelligence, and secure your financial legacy on the Zero-Blockchain.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <GlassButton
                onClick={() => navigate('/dashboard')}
                variant="purple"
                size="lg"
                className="px-8 py-6"
              >
                Design Portfolio <TiArrowRight className="text-2xl" />
              </GlassButton>
              
              <GlassButton
                onClick={() => window.open('/Whitepapers/Zero-Wealth_Report_WP_Draft.docx')}
                variant="gray"
                size="lg"
                className="px-8 py-6"
              >
                Wealth Whitepaper <TiFlash className="text-2xl text-purple-400" />
              </GlassButton>
            </div>

            <div className="mt-20 flex flex-wrap justify-center gap-10 opacity-20 grayscale hover:grayscale-0 transition-all duration-700">
              <div className="flex items-center gap-2 font-bold tracking-widest text-[10px]"><FaEye /> REAL-TIME AUDIT</div>
              <div className="flex items-center gap-2 font-bold tracking-widest text-[10px]"><FaLock /> MULTI-SIG VAULT</div>
              <div className="flex items-center gap-2 font-bold tracking-widest text-[10px]"><FaRobot /> AI OPTIMIZED</div>
            </div>
          </div>
        </motion.div>

      </div>

      {/* Back to Top */}
      {isBottom && (
        <div
          ref={scrollTopRef}
          className="fixed bottom-10 left-5 flex items-center gap-3 px-5 py-3 bg-black/40 backdrop-blur-sm border border-purple-500/30 rounded-full shadow-lg shadow-purple-500/10 cursor-pointer hover:bg-black/60 hover:border-purple-400/50 transition-all duration-500 z-50 group"
          onClick={scrollToTop}
        >
          <TiArrowUp className="text-purple-400 group-hover:-translate-y-1 transition-transform duration-500" size={24} />
          <span className="relative z-20 text-sm uppercase text-white/80 font-medium tracking-wider">BACK TO TOP</span>
        </div>
      )}

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Portfolio;
